import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnFiltersState, PaginationState } from '@tanstack/react-table';
import { addDays, format, parse } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useUser } from '@r/api/auth.tsx';
import { adminChangelogs, useApiAdminchangelogsIdDelete } from '@r/api/generated/client.ts';
import type {
  AdminChangelogsParams,
  ChangelogChangelogReadModel
} from '@r/api/generated/client.schemas.ts';
import {
  ADMIN_CHANGELOGS_LIST_QUERY_KEY,
  invalidateChangelogQueries
} from '@r/components/changelogs/invalidate-changelog-queries.ts';
import { DataTableAdvancedToolbar } from '@r/components/data-table/advanced/data-table-advanced-toolbar.tsx';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { useOrvalErrorToast, useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { buildApiDateRangeParams } from '@r/utils/date-range-filter.ts';
import { getAdminTablePersistenceKey } from '@r/utils/data-table-persistence.ts';
import { ChangelogDialog } from '@admin/components/forms/changelogs/changelog-dialog.tsx';

import { useChangelogsColumns, useChangelogsFilterFields } from './changelogs-columns.tsx';

type Sort = 'asc' | 'desc' | undefined;

type ChangelogFilters = {
  title: string;
  tag: string[] | undefined;
  target: string[] | undefined;
  createdAt: string;
  createdAtAfter: string;
  createdAtBefore: string;
  titleSortValue: Sort;
  tagSortValue: Sort;
  targetSortValue: Sort;
  createdAtSortValue: Sort;
};

type ChangelogsQueryResult = {
  data: ChangelogChangelogReadModel[];
  pagination?: { last?: number; totalItems?: number };
};

/**
 * Charge, filtre, trie et pagine les entrées de changelog côté serveur, et expose
 * l’édition / la suppression.
 */
export function ChangelogsTable() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showOrvalErrorToast = useOrvalErrorToast();
  const user = useUser();
  const [changelogToEdit, setChangelogToEdit] = useState<ChangelogChangelogReadModel | null>(null);

  const deleteChangelog = useApiAdminchangelogsIdDelete({
    mutation: {
      onSuccess: async () => {
        await invalidateChangelogQueries(queryClient);
        toast.success(t('fiddy.admin.changelog.delete.success'));
      },
      onError: (error) => {
        showOrvalErrorToast(error, t('fiddy.admin.changelog.delete.error'));
      }
    }
  });

  /**
   * Demande confirmation avant de déclencher la suppression côté API.
   */
  const handleDelete = (changelog: ChangelogChangelogReadModel) => {
    toast(t('fiddy.admin.changelog.delete.confirm', { title: changelog.title }), {
      action: {
        label: t('fiddy.admin.changelog.delete.confirm.action'),
        onClick: () => deleteChangelog.mutate({ id: String(changelog.id) })
      },
      cancel: {
        label: t('fiddy.admin.changelog.delete.cancel.action'),
        onClick: () => {}
      }
    });
  };

  async function changelogsListFiltersQuery(
    filters: ChangelogFilters,
    page: number | null = null,
    limit: number | null = null
  ): Promise<ChangelogsQueryResult> {
    const {
      title,
      tag,
      target,
      createdAt,
      createdAtAfter,
      createdAtBefore,
      titleSortValue,
      tagSortValue,
      targetSortValue,
      createdAtSortValue
    } = filters;

    const params: AdminChangelogsParams = {
      page: page ?? pageIndex + 1,
      perPage: limit ?? pageSize,
      ...(title ? { title } : {}),
      ...(tag?.length ? { 'tag[]': tag } : {}),
      ...(target?.length ? { 'target[]': target } : {}),
      ...(createdAtAfter ? { 'createdAt[after]': createdAtAfter } : {}),
      // `[before]` compare à minuit : envoyer la date de fin telle quelle exclurait tout
      // ce qui est créé ce jour-là. On décale d'un jour et bascule sur `strictly_before`
      // pour inclure la journée entière (même logique que `buildApiDateRangeParams`).
      ...(createdAtBefore
        ? {
            'createdAt[strictly_before]': format(
              addDays(parse(createdAtBefore, 'yyyy-MM-dd', new Date()), 1),
              'yyyy-MM-dd'
            )
          }
        : {}),
      ...(!createdAtAfter && !createdAtBefore
        ? buildApiDateRangeParams('createdAt', createdAt)
        : {}),
      ...(titleSortValue ? { 'order[title]': titleSortValue } : {}),
      ...(tagSortValue ? { 'order[tag]': tagSortValue } : {}),
      ...(targetSortValue ? { 'order[target]': targetSortValue } : {}),
      ...(createdAtSortValue ? { 'order[createdAt]': createdAtSortValue } : {})
    };

    const response = await adminChangelogs(params);
    return { data: response.data ?? [], pagination: response.pagination };
  }

  function changelogsListFilters(): ChangelogFilters {
    const title = (columnFilters.find((filter) => filter.id === 'title')?.value as string) ?? '';
    const tag = columnFilters.find((filter) => filter.id === 'tag')?.value as string[] | undefined;
    const target = columnFilters.find((filter) => filter.id === 'target')?.value as
      | string[]
      | undefined;
    const createdAt =
      (columnFilters.find((filter) => filter.id === 'createdAt')?.value as string) ?? '';
    const createdAtAfter =
      (columnFilters.find((filter) => filter.id === 'createdAtAfter')?.value as string) ?? '';
    const createdAtBefore =
      (columnFilters.find((filter) => filter.id === 'createdAtBefore')?.value as string) ?? '';

    const sortValue = (id: string): Sort => {
      const sort = sorting.find((s) => s.id === id);
      return sort ? (sort.desc ? 'desc' : 'asc') : undefined;
    };

    return {
      title,
      tag,
      target,
      createdAt,
      createdAtAfter,
      createdAtBefore,
      titleSortValue: sortValue('title'),
      tagSortValue: sortValue('tag'),
      targetSortValue: sortValue('target'),
      createdAtSortValue: sortValue('createdAt')
    };
  }

  const changelogsListQuery = useQuery<ChangelogsQueryResult>({
    queryKey: ADMIN_CHANGELOGS_LIST_QUERY_KEY,
    queryFn: () => changelogsListFiltersQuery(changelogsListFilters())
  });

  const filterFields = useChangelogsFilterFields();

  const changelogsTablePersistenceKey = useMemo(
    // Clé de persistance isolée par utilisateur pour conserver ses préférences.
    () => getAdminTablePersistenceKey('changelogs', user.data?.id),
    [user.data?.id]
  );

  const { table } = useDataTable<ChangelogChangelogReadModel, unknown>({
    data: changelogsListQuery.data?.data ?? [],
    columns: useChangelogsColumns(setChangelogToEdit, handleDelete),
    filterFields,
    pageCount: changelogsListQuery.data?.pagination?.last ?? 1,
    enableAdvancedFilter: true,
    persistence: {
      // Persiste pagination, tri et filtres en localStorage.
      key: changelogsTablePersistenceKey
    }
  });

  const { pageIndex, pageSize }: PaginationState = table.getState().pagination as PaginationState;
  const sorting = table.getState().sorting;
  const columnFilters: ColumnFiltersState = table.getState().columnFilters;
  const prevValuesRef = useRef({ pageIndex, pageSize, columnFilters, sorting });

  useEffect(() => {
    const prevValues = prevValuesRef.current;

    if (
      pageIndex != prevValues.pageIndex ||
      pageSize != prevValues.pageSize ||
      JSON.stringify(columnFilters) !== JSON.stringify(prevValues.columnFilters) ||
      JSON.stringify(sorting) != JSON.stringify(prevValues.sorting)
    ) {
      changelogsListQuery.refetch();
    }

    prevValuesRef.current = { pageIndex, pageSize, columnFilters, sorting };
  }, [pageIndex, pageSize, columnFilters, sorting]);

  useOrvalQueryErrorToast(
    {
      isError: changelogsListQuery.isError,
      error: changelogsListQuery.error,
      errorUpdatedAt: changelogsListQuery.errorUpdatedAt
    },
    t('fiddy.admin.changelog.list.error')
  );

  if (changelogsListQuery.isLoading) {
    return (
      <DataTableSkeleton
        columnCount={6}
        cellWidths={['16rem', '10rem', '10rem', '10rem', '4rem', '3rem']}
      />
    );
  }

  return (
    <>
      <DataTableAdvancedToolbar table={table} filterFields={filterFields} />
      <DataTable table={table} totalItems={changelogsListQuery.data?.pagination?.totalItems} />

      <ChangelogDialog
        isOpen={changelogToEdit !== null}
        changelog={changelogToEdit}
        onOpenChange={(open) => {
          if (!open) setChangelogToEdit(null);
        }}
      />
    </>
  );
}
